import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "video",
    "scene",
    "progress",
    "status",
    "playIcon",
    "pauseIcon",
    "fallback",
    "toggle",
    "media",
    "controls",
    "restartButton",
    "fullscreenButton",
    "seek",
    "currentTime",
    "durationTime"
  ]

  connect() {
    this.currentIndex = 0
    this.isExternalVideo = false
    this.isSeeking = false
    this.controlsPinned = false
    this.userPaused = false
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.fullscreenChangeHandler = () => this.updateFullscreenState()
    document.addEventListener("fullscreenchange", this.fullscreenChangeHandler)
    document.addEventListener("webkitfullscreenchange", this.fullscreenChangeHandler)
    this.activate(0, { autoplay: !this.reducedMotion })
  }

  disconnect() {
    this.stopProgressLoop()
    this.clearControlsHideTimer()
    document.removeEventListener("fullscreenchange", this.fullscreenChangeHandler)
    document.removeEventListener("webkitfullscreenchange", this.fullscreenChangeHandler)
  }

  select(event) {
    const index = Number(event.currentTarget.dataset.sceneIndex)
    this.userPaused = false
    this.activate(index, { autoplay: true })
  }

  keydown(event) {
    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.activate((this.currentIndex + 1) % this.sceneTargets.length, { autoplay: true })
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.activate((this.currentIndex - 1 + this.sceneTargets.length) % this.sceneTargets.length, { autoplay: true })
    }
  }

  toggle() {
    if (this.isExternalVideo) return

    this.revealControls()

    if (this.videoTarget.paused) {
      this.userPaused = false
      this.playCurrent()
    } else {
      this.userPaused = true
      this.videoTarget.pause()
    }
  }

  restart() {
    if (this.isExternalVideo) return

    this.revealControls()
    this.videoTarget.currentTime = 0
    this.setProgress(0)
    this.updateTimeLabels(0, this.currentDuration())
    this.userPaused = false
    this.playCurrent()
  }

  seek(event) {
    if (this.isExternalVideo) return

    this.revealControls()
    const duration = this.currentDuration()
    if (!duration) return

    const progress = Number(event.currentTarget.value) / 1000
    const nextTime = duration * Math.max(0, Math.min(progress, 1))
    this.videoTarget.currentTime = nextTime
    this.setProgress(nextTime / duration)
    this.updateTimeLabels(nextTime, duration)
  }

  beginSeek() {
    this.isSeeking = true
    this.revealControls()
  }

  endSeek(event) {
    this.seek(event)
    this.isSeeking = false
    this.syncProgress()
    this.scheduleControlsHide()
  }

  revealControls() {
    this.element.classList.remove("is-controls-hidden")
    this.scheduleControlsHide()
  }

  pinControls() {
    this.controlsPinned = true
    this.clearControlsHideTimer()
    this.element.classList.remove("is-controls-hidden")
  }

  unpinControls() {
    this.controlsPinned = false
    this.scheduleControlsHide()
  }

  toggleFullscreen() {
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement

    if (fullscreenElement) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen
      if (exit) exit.call(document)
      return
    }

    const target = this.hasMediaTarget ? this.mediaTarget : this.element
    const request = target.requestFullscreen || target.webkitRequestFullscreen
    if (request) request.call(target)
  }

  ended() {
    if (this.userPaused) return

    // YouTube iframe 활성 상태에서는 ended 이벤트가 없으므로 auto-advance 하지 않음
    const activeIframe = this.videoTarget.parentElement?.querySelector("iframe.yt-iframe")
    if (activeIframe && activeIframe.style.display !== "none") return

    this.activate((this.currentIndex + 1) % this.sceneTargets.length, { autoplay: !this.reducedMotion })
  }

  error() {
    // YouTube 재생 중이면 무시
    const activeIframe = this.videoTarget.parentElement?.querySelector("iframe.yt-iframe")
    if (activeIframe && activeIframe.style.display !== "none") return

    this.hasVideoError = true
    this.stopProgressLoop()
    this.clearControlsHideTimer()
    this.setProgress(0)
    this.setPlaying(false)
    this.updateStatus("preparing")
    if (this.hasFallbackTarget) this.fallbackTarget.hidden = false
  }

  loaded() {
    if (this.hasFallbackTarget) this.fallbackTarget.hidden = true
    this.setVideoControlsEnabled(!this.isExternalVideo)
    this.syncProgress()
  }

  playing() {
    this.setPlaying(true)
    this.updateStatus("playing")
    this.revealControls()
    this.startProgressLoop()
  }

  paused() {
    this.setPlaying(false)
    this.clearControlsHideTimer()
    this.element.classList.remove("is-controls-hidden")
    if (!this.videoTarget.ended) {
      this.updateStatus(this.hasVideoError || this.videoTarget.error ? "preparing" : "paused")
    }
    this.stopProgressLoop()
    this.syncProgress()
  }

  syncProgress() {
    const duration = this.currentDuration()
    const currentTime = duration ? this.videoTarget.currentTime : 0
    const progress = duration ? currentTime / duration : 0
    this.setProgress(progress)
    this.updateTimeLabels(currentTime, duration)
  }

  activate(index, options = {}) {
    const scene = this.sceneTargets[index]
    if (!scene) return

    this.currentIndex = index
    this.clearControlsHideTimer()
    this.element.classList.remove("is-controls-hidden")
    this.updateSceneButtons(index)
    this.applyAspectRatio(scene.dataset.aspectRatio)
    this.swapVideo(scene)
    this.setProgress(0)
    this.updateTimeLabels(0, 0)

    if (options.autoplay) {
      this.playCurrent()
    } else {
      this.updateStatus("ready")
      this.setPlaying(false)
    }
  }

  updateSceneButtons(index) {
    this.sceneTargets.forEach((scene, sceneIndex) => {
      const active = sceneIndex === index
      scene.classList.toggle("is-active", active)
      scene.setAttribute("aria-selected", active ? "true" : "false")
      scene.tabIndex = active ? 0 : -1
    })
  }

  applyAspectRatio(aspectRatio) {
    const normalized = this.normalizeAspectRatio(aspectRatio)
    this.element.style.setProperty("--home-scene-aspect", normalized)
  }

  normalizeAspectRatio(aspectRatio) {
    const value = String(aspectRatio || "").trim()
    return /^\d+(?:\.\d+)?\s*\/\s*\d+(?:\.\d+)?$/.test(value) ? value : "16 / 9"
  }

  swapVideo(scene) {
    const youtubeId = scene.dataset.youtubeId

    this.stopProgressLoop()
    this.hasVideoError = false
    if (this.hasFallbackTarget) this.fallbackTarget.hidden = true

    if (youtubeId) {
      this.isExternalVideo = true
      this.setVideoControlsEnabled(false)
      // YouTube iframe으로 교체
      this.videoTarget.style.display = "none"
      let iframe = this.videoTarget.parentElement.querySelector("iframe.yt-iframe")
      if (!iframe) {
        iframe = document.createElement("iframe")
        iframe.className = "yt-iframe"
        iframe.setAttribute("frameborder", "0")
        iframe.setAttribute("allowfullscreen", "")
        iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture")
        iframe.style.width = "100%"
        iframe.style.height = "100%"
        iframe.style.position = "absolute"
        iframe.style.top = "0"
        iframe.style.left = "0"
        this.videoTarget.parentElement.appendChild(iframe)
      }
      iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&controls=1&rel=0`
      iframe.style.display = "block"
    } else {
      this.isExternalVideo = false
      // 기존 <video> 방식
      const existingIframe = this.videoTarget.parentElement.querySelector("iframe.yt-iframe")
      if (existingIframe) {
        existingIframe.src = "" // 재생 중지
        existingIframe.style.display = "none"
      }

      const src = scene.dataset.videoSrc
      const poster = scene.dataset.posterSrc

      this.videoTarget.style.display = ""
      if (poster) this.videoTarget.poster = poster
      this.setVideoControlsEnabled(Boolean(src))

      if (src && this.videoTarget.getAttribute("src") !== src) {
        this.videoTarget.setAttribute("src", src)
      }

      this.videoTarget.load()
    }
  }

  playCurrent() {
    if (this.isExternalVideo) return

    this.videoTarget.muted = true
    const playPromise = this.videoTarget.play()

    if (playPromise?.catch) {
      playPromise.catch(() => {
        this.setPlaying(false)
        this.updateStatus(this.hasVideoError || this.videoTarget.error ? "preparing" : "paused")
      })
    }
  }

  setPlaying(isPlaying) {
    if (this.hasPlayIconTarget) this.playIconTarget.hidden = isPlaying
    if (this.hasPauseIconTarget) this.pauseIconTarget.hidden = !isPlaying
    if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-pressed", isPlaying ? "true" : "false")
  }

  setVideoControlsEnabled(enabled) {
    if (this.hasToggleTarget) this.toggleTarget.disabled = !enabled
    if (this.hasRestartButtonTarget) this.restartButtonTarget.disabled = !enabled
    if (this.hasSeekTarget) this.seekTarget.disabled = !enabled
    this.element.classList.toggle("has-disabled-video-controls", !enabled)
    if (!enabled) {
      this.clearControlsHideTimer()
      this.element.classList.remove("is-controls-hidden")
    }
  }

  updateStatus(state) {
    if (!this.hasStatusTarget) return

    const labels = {
      ready: this.element.dataset.readyLabel || "Ready",
      playing: this.element.dataset.playingLabel || "Playing",
      paused: this.element.dataset.pausedLabel || "Paused",
      preparing: this.element.dataset.preparingLabel || "Preparing"
    }

    this.statusTarget.textContent = labels[state] || labels.ready
  }

  startProgressLoop() {
    this.stopProgressLoop()

    const tick = () => {
      this.syncProgress()
      this.progressFrame = window.requestAnimationFrame(tick)
    }

    this.progressFrame = window.requestAnimationFrame(tick)
  }

  stopProgressLoop() {
    if (!this.progressFrame) return

    window.cancelAnimationFrame(this.progressFrame)
    this.progressFrame = null
  }

  scheduleControlsHide() {
    this.clearControlsHideTimer()
    if (this.shouldKeepControlsVisible()) return

    this.controlsHideTimer = window.setTimeout(() => {
      if (!this.shouldKeepControlsVisible()) this.element.classList.add("is-controls-hidden")
    }, 1800)
  }

  clearControlsHideTimer() {
    if (!this.controlsHideTimer) return

    window.clearTimeout(this.controlsHideTimer)
    this.controlsHideTimer = null
  }

  shouldKeepControlsVisible() {
    if (this.isExternalVideo || this.hasVideoError || this.isSeeking || this.controlsPinned) return true
    if (!this.hasVideoTarget || this.videoTarget.paused || this.videoTarget.ended) return true
    if (this.hasControlsTarget && this.controlsTarget.contains(document.activeElement)) return true

    return false
  }

  setProgress(progress) {
    const normalized = Math.max(0, Math.min(progress, 1))
    const progressPercent = `${normalized * 100}%`

    this.progressTargets.forEach((target, index) => {
      target.style.transform = `scaleX(${index === this.currentIndex ? normalized : 0})`
    })

    if (this.hasSeekTarget) {
      if (!this.isSeeking) this.seekTarget.value = Math.round(normalized * 1000)
      this.seekTarget.style.setProperty("--home-video-progress", progressPercent)
    }
  }

  currentDuration() {
    const duration = this.videoTarget.duration
    return duration && Number.isFinite(duration) ? duration : 0
  }

  updateTimeLabels(currentTime = 0, duration = 0) {
    if (this.hasCurrentTimeTarget) this.currentTimeTarget.textContent = this.formatTime(currentTime)
    if (this.hasDurationTimeTarget) this.durationTimeTarget.textContent = this.formatTime(duration)
  }

  formatTime(seconds) {
    if (!seconds || !Number.isFinite(seconds)) return "0:00"

    const totalSeconds = Math.max(0, Math.floor(seconds))
    const minutes = Math.floor(totalSeconds / 60)
    const remainingSeconds = String(totalSeconds % 60).padStart(2, "0")
    return `${minutes}:${remainingSeconds}`
  }

  updateFullscreenState() {
    if (!this.hasFullscreenButtonTarget) return

    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement
    const isFullscreen = fullscreenElement === this.mediaTarget || fullscreenElement === this.element
    this.fullscreenButtonTarget.setAttribute("aria-pressed", isFullscreen ? "true" : "false")
  }
}
